/**
 * Fixture — small electrical fixtures, kinds in ./fixtureKinds.ts (the objectKinds
 * pattern — one source for symbols, bodies, and defaults). TWO HOSTING MODELS
 * live side by side in this one category, kind-gated by `conditionalOn`:
 *
 * WALL kinds (outlet/switch/sconce/lowVoltage, WALL_FIXTURE_KINDS — the original
 * ELECTRICAL FIXTURES round, 2026-08): hosted like openings (hostId +
 * hostOffsetFt station along the host's stored path — the rehost machinery in
 * commands/geometryOps.ts rides `hostedElements()` and covers fixtures for free)
 * but the fixture CUTS NOTHING — no `cutsHost`, so the wall generator never sees it
 * (no void, no header/sill machinery) — and mounts on a FACE of the wall via the
 * `sideFlip` instance param (casework's side convention: false = the interior
 * face, true = flipped). Curved walls are deliberately ALLOWED (unlike openings'
 * straight-segment exclusion): the fixture is point-like at wall scale, so its
 * station point + local tangent on the FLATTENED path place it correctly on an arc.
 * mountHeightFt is PURELY an instance param (Matt, 2026-08-12 — a fixture TYPE is
 * its kind + appearance; mounting height is WHERE this one sits), seeded from the
 * kind's code-height standard (`FIXTURE_MOUNT_STANDARD_FT`) at CREATION time
 * (`commands/elements.ts#seededFixtureMountHeight`).
 *
 * CEILING kinds (recessedCan/pendant/flushMount, CEILING_FIXTURE_KINDS — moved
 * here from the `object` category, 2026-08-23, Matt: "Lights should be under
 * electrical fixtures, not objects... We already have sconces there."): a
 * ceiling is a 2D area, not a path, so the wall model's 1D station doesn't fit —
 * these use the OBJECT anchor model instead (`path`, a single-node 2D point, +
 * `rotationDeg` — objectKinds.ts's exact frame/param shape, so translateElement/
 * duplicateElement/mirror-with-own-path all already work unchanged) PLUS a real
 * `hostId` pointing at the specific ceiling element underneath — cascade-delete
 * and "moves with its ceiling" fall out for free from doc.ts's already-generic
 * `hostedElements()`/`deleteElement()`. Height is DERIVED from the host
 * ceiling's own resolved z, never stored (fixtureSolid/fixtureZSpan,
 * generate3d.ts). `widthFt`/`depthFt`/`heightFt` (+ `trimShape` for recessedCan)
 * are TYPE params for these kinds, carried over from objectKinds.ts verbatim.
 *
 * Parameter transfer (the standing param-kind rules): station/side/mount-height
 * (wall kinds) and rotation (ceiling kinds) are all POSITIONAL — copying any of
 * them would MOVE the target rather than restyle it. `path` is kind 'path2d',
 * excluded from eyedropper transfer automatically (the object precedent).
 */
import type { CategorySchema } from '../../registry'
import {
  FIXTURE_KINDS, FIXTURE_LIGHT_KINDS, WALL_FIXTURE_KINDS, CEILING_FIXTURE_KINDS, RECESSED_TRIM_SHAPES,
  SIZED_WALL_FIXTURE_KINDS, POINT_FIXTURE_KINDS, GROUND_FIXTURE_KINDS, SWITCH_DEVICES, MAX_SWITCH_GANGS,
} from './fixtureKinds'
import { LIGHT_STYLES, STYLED_LIGHT_KINDS, HANGING_LIGHT_KINDS, lightStyleChoices } from './lightStyles'
import { FIXTURE_FAMILY_CHOICES } from '../../families'
import type { ParamDef } from '../../params'
import { SERVICE_AMP_CHOICES, DEFAULT_SERVICE_AMPS } from '../../siteItemLabels'

/**
 * LIGHT STYLES (2026-10-07): one STYLE choice per styled light kind — its own key, so each
 * kind's dropdown lists only its own styles (a pendant never offers a gooseneck). Instance
 * params: the style is WHAT this one looks like, the type is the generic fixture + symbol.
 * Not positional — the eyedropper carries a style from one sconce to the next.
 */
const STYLE_PARAMS: ParamDef[] = STYLED_LIGHT_KINDS.map((kind) => ({
  key: LIGHT_STYLES[kind].param, label: 'Style', kind: 'choice' as const,
  default: LIGHT_STYLES[kind].styles[0].value,
  choices: lightStyleChoices(kind),
  conditionalOn: { param: 'fixtureKind', equals: kind },
}))

/** the kinds whose 3D comes from a style — the type's Material doesn't apply to them (their
 *  metal takes a color from the Paint Brush instead) */
const UNSTYLED_KINDS = FIXTURE_KINDS.map((k) => k.value as string).filter((k) => !STYLED_LIGHT_KINDS.includes(k))

export const fixtureSchema: CategorySchema = {
  id: 'fixture',
  // fixtures sit ON walls/ceilings — they win overlapping picks like openings do
  pickRank: 0,
  label: 'Fixture',
  hostable: { hostsInto: ['wall', 'ceiling'] },
  typeParams: {
    groups: [
      {
        label: 'Fixture',
        params: [
          {
            key: 'fixtureKind', label: 'Kind', kind: 'choice', default: 'outlet',
            choices: FIXTURE_KINDS.map((k) => ({ value: k.value, label: k.label })),
          },
          // FAMILY CATEGORIES round: '' = Auto (derive from Kind) — see
          // objectSchema's identical familyCategory param for the full note.
          {
            key: 'familyCategory', label: 'Family', kind: 'choice', default: '',
            choices: FIXTURE_FAMILY_CHOICES.map((v) => ({ value: v, label: v || 'Auto (from Kind)' })),
          },
          {
            // ceiling-light follow-up (Matt: "round and square recessed lights"): the
            // trim/aperture shape — drives the plan glyph and the 3D housing profile.
            // Size is the ordinary widthFt/depthFt (curated 2"/4"/6" seeds), not this.
            key: 'trimShape', label: 'Trim Shape', kind: 'choice', default: 'round',
            choices: RECESSED_TRIM_SHAPES.map((s) => ({ value: s.value, label: s.label })),
            conditionalOn: { param: 'fixtureKind', equals: 'recessedCan' },
          },
          // FIT-OUT CATALOG: the SIZED wall kinds (grab bars, mirrors, panels…) carry
          // their size on the type too; 0/unset falls back to the kind's own default
          {
            key: 'widthFt', label: 'Width', kind: 'length', default: 2, min: 0.05, max: 12,
            conditionalOn: { param: 'fixtureKind', equals: [...POINT_FIXTURE_KINDS, ...SIZED_WALL_FIXTURE_KINDS] },
          },
          {
            key: 'depthFt', label: 'Depth', kind: 'length', default: 2, min: 0.02, max: 8,
            conditionalOn: { param: 'fixtureKind', equals: [...POINT_FIXTURE_KINDS, ...SIZED_WALL_FIXTURE_KINDS] },
          },
          {
            key: 'heightFt', label: 'Height', kind: 'length', default: 2.5, min: 0.05, max: 8,
            conditionalOn: { param: 'fixtureKind', equals: [...CEILING_FIXTURE_KINDS, ...SIZED_WALL_FIXTURE_KINDS] },
          },
        ],
      },
      {
        label: 'Appearance',
        params: [
          {
            key: 'surfaceMaterial', label: 'Material', kind: 'material', default: '',
            conditionalOn: { param: 'fixtureKind', equals: UNSTYLED_KINDS },
          },
        ],
      },
      {
        label: 'Lighting',
        params: [
          {
            // SWITCH-PAIRED LIGHTING round (Round C, docs/bim-plan/18 §6): a visual
            // brightness scalar for the walkthrough's real light — photometric-ISH by
            // design (the ask's out-of-scope line: no load calcs, just on/off looks).
            key: 'lightIntensity', label: 'Light Brightness', kind: 'number', default: 1,
            min: 0.2, max: 3, conditionalOn: { param: 'fixtureKind', equals: [...FIXTURE_LIGHT_KINDS] },
          },
          {
            // LIGHTING FIXTURE SCHEDULE (docs/bim-plan/28 round 3): what the schedule's LAMP
            // column prints (e.g. "LED 2700K 12W") — free text, the firm's own spec words
            key: 'lamp', label: 'Lamp', kind: 'text', default: '',
            conditionalOn: { param: 'fixtureKind', equals: [...FIXTURE_LIGHT_KINDS] },
          },
          {
            // ...and its REMARKS column (dimmer, wet location, owner-furnished …)
            key: 'scheduleNote', label: 'Schedule Remarks', kind: 'text', default: '',
            conditionalOn: { param: 'fixtureKind', equals: [...FIXTURE_LIGHT_KINDS] },
          },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Style',
        params: [
          ...STYLE_PARAMS,
          {
            // HANG HEIGHT (2026-10-08): hanging lights — the BOTTOM of the fixture above its
            // level (e.g. 30" over an island, 7'-0" in a stair hall). Set, the drop below the
            // ceiling follows from the ceiling's height (doc.ts); empty = use Overall Drop
            key: 'bottomHeightFt', label: 'Bottom Height', kind: 'length', default: null, optional: true,
            optionalHint: 'use Overall Drop', min: 0, max: 40,
            conditionalOn: { param: 'fixtureKind', equals: [...HANGING_LIGHT_KINDS] },
          },
          {
            // hanging lights: ceiling → bottom of the fixture. Empty = the style's own
            // standard drop (a pendant 30", a coach lantern 44"…). Ignored when a Bottom Height
            // is set (that derives the drop)
            key: 'dropFt', label: 'Overall Drop', kind: 'length', default: null, optional: true,
            optionalHint: 'style default', min: 0.5, max: 20,
            conditionalOn: { param: 'fixtureKind', equals: [...HANGING_LIGHT_KINDS] },
          },
          {
            // the Paint Brush's color on the fixture's metal (a colors.ts ref; null = the
            // style's own finish, or the project standard for light fixtures). Brushed, never
            // typed — hidden from the form like a cabinet's paintColor.
            key: 'paintColor', label: 'Color', kind: 'data', default: null, hidden: true,
            conditionalOn: { param: 'fixtureKind', equals: [...STYLED_LIGHT_KINDS] },
          },
        ],
      },
      {
        label: 'Placement',
        params: [
          // CASEWORK STUDIO stage 4: the box a Studio-placed light belongs to, and its role
          { key: 'studioBoxId', label: 'Studio Box', kind: 'data', default: null, hidden: true },
          { key: 'studioRole', label: 'Studio Role', kind: 'data', default: null, hidden: true },
          { key: 'studioPlace', label: 'Studio Place', kind: 'data', default: null, hidden: true },
          {
            key: 'hostOffsetFt', label: 'Position Along Wall', kind: 'length', default: 0, min: 0, max: 10000, positional: true,
            conditionalOn: { param: 'fixtureKind', equals: [...WALL_FIXTURE_KINDS] },
          },
          {
            // the mounted FACE: false = the interior face (casework's side
            // convention), true = the other face. Placement writes it from the
            // cursor side; the palette toggle flips a placed fixture. POSITIONAL —
            // copying a side re-mounts the target through the wall.
            key: 'sideFlip', label: 'Flip to Other Face', kind: 'bool', default: false, positional: true,
            conditionalOn: { param: 'fixtureKind', equals: [...WALL_FIXTURE_KINDS] },
          },
          {
            // plain instance value, to device CENTER — seeded from the kind's code
            // standard at creation (commands/elements.ts#seededFixtureMountHeight),
            // ordinary and editable from then on. The bare schema default (1') only
            // matters for a synthetic caller that skips creation entirely.
            key: 'mountHeightFt', label: 'Mount Height', kind: 'length', default: 1,
            min: 0.1, max: 12, positional: true,
            conditionalOn: { param: 'fixtureKind', equals: [...WALL_FIXTURE_KINDS] },
          },
          {
            // GANGED SWITCHES (2026-10-14): how many device positions this switch box holds —
            // the plate and the in-wall box grow with it (fixtureKinds.ts#deviceBoxFt)
            key: 'gangs', label: 'Gangs', kind: 'number', default: 1, min: 1, max: MAX_SWITCH_GANGS,
            conditionalOn: { param: 'fixtureKind', equals: 'switch' },
          },
          {
            // what sits in each gang: one toggle, a stacked 2-in-1 / 3-in-1 device, or a
            // scene keypad
            key: 'switchDevice', label: 'Device per Gang', kind: 'choice', default: 'standard',
            choices: SWITCH_DEVICES.map((d) => ({ value: d.value, label: d.label })),
            conditionalOn: { param: 'fixtureKind', equals: 'switch' },
          },
          {
            // SITE ITEM LABELS (2026-10-20, Matt: "Allow us to set the amperage and have the main
            // service amperage part of the note"): a meter/main's service size — the site plan's
            // label reads "200A ELECTRICAL METER/MAIN" (siteItemLabels.ts)
            key: 'serviceAmps', label: 'Service Size (Amps)', kind: 'choice', default: DEFAULT_SERVICE_AMPS,
            choices: SERVICE_AMP_CHOICES.map((a) => ({ value: a, label: `${a} A` })),
            conditionalOn: { param: 'fixtureKind', equals: 'meter' },
          },
          {
            // CEILING HOSTING round (2026-08-23): single-node anchor — the object
            // `path` param verbatim, so translateElement/duplicateElement/eyedropper-
            // exclusion all already work unchanged. World XY only; z derives from the
            // host ceiling (fixtureSolid). LIGHT STYLES: ground kinds use it too.
            key: 'path', label: 'Anchor', kind: 'path2d', default: null,
            conditionalOn: { param: 'fixtureKind', equals: [...POINT_FIXTURE_KINDS] },
          },
          {
            // 0 faces world +y; the rotate grip and placement aim write this — the
            // object rotationDeg param verbatim (trim/shade orientation). POSITIONAL.
            key: 'rotationDeg', label: 'Rotation', kind: 'number', default: 0,
            min: -360, max: 360, positional: true,
            conditionalOn: { param: 'fixtureKind', equals: [...POINT_FIXTURE_KINDS] },
          },
          {
            // LIGHT STYLES (2026-10-07): a ground light's base above its level — 0 on the
            // ground, a pier or wall cap's height for a pier-top lantern. POSITIONAL.
            key: 'baseOffsetFt', label: 'Base Height', kind: 'length', default: 0,
            min: -40, max: 40, positional: true,
            conditionalOn: { param: 'fixtureKind', equals: [...GROUND_FIXTURE_KINDS] },
          },
          {
            // SWITCH-PAIRED LIGHTING round (Round C, docs/bim-plan/18 §6 + §9): the
            // PICKED light-fixture element ids this SWITCH controls — the topAttachIds
            // model verbatim (wall/schema.ts precedent). CONTROL, never circuit
            // membership (§9's semantic-purity discipline: the future circuits entity
            // is its own record kind, additive on top of this). Managed by the pet
            // palette's "Pair lights…" pick flow, never a form field; positional (a
            // relationship to THIS switch's room); dangling ids degrade to a skip.
            // Meaningful only when fixtureKind === 'switch' — harmlessly inert
            // otherwise. Empty/null = the switch controls nothing.
            key: 'switchTargetIds', label: 'Controlled Lights', kind: 'data',
            default: null, positional: true, hidden: true,
          },
        ],
      },
    ],
  },
}
